/**
 * Cliente de la busqueda general. **Una llamada, y es GET.**
 *
 * La unica puerta a la red de este archivo es `api.fetchReadOnly`, que no acepta
 * metodo ni cuerpo: buscar es leer, y desde aca no hay forma de escribir nada
 * aunque alguien quisiera. Mismo criterio —y mismo motivo— que el cliente del
 * chat de ayuda (`components/guia/api.ts`). Del otro lado, la vista del backend
 * declara `get()` y ningun otro verbo, asi que cualquier intento contesta 405.
 *
 * NADA DEL VOCABULARIO DEL OFICIO ESTA ESCRITO ACA. Los rotulos
 * («Administraciones», «Consorcios»), los iconos, el orden de los grupos y la
 * lista de tipos elegibles vienen TODOS en la respuesta. Es la misma decision
 * que sostiene el widget «¿Que es este contacto?»: el frontend es generico y no
 * tiene por que saber que existe una cosa llamada administracion. El dia que
 * aparezca un cuarto nivel, aparece solo en la pantalla.
 */

import { api } from '@/lib/api';

/** Un eslabon del camino de un resultado. Es navegable: modelo + id. */
export interface PasoDelCamino {
  etiqueta: string;
  modelo: string;
  id: number;
}

/** Por que entro esta fila. Lo decide el servidor; aca solo se dibuja. */
export type MotivoDeCoincidencia = 'propio' | 'administracion' | 'administrador';

export interface CoincidenciaDeBusqueda {
  tipo: string;
  modelo: string;
  id: number;
  titulo: string;
  numero: string;
  detalle: string;
  motivo: MotivoDeCoincidencia;
  camino: PasoDelCamino[];
}

export interface GrupoDeBusqueda {
  tipo: string;
  modelo: string;
  etiqueta: string;
  etiqueta_singular: string;
  icono: string;
  /** Cuantos hay en la base. Puede ser mayor que `resultados.length`. */
  total: number;
  /**
   * `false` cuando el usuario no tiene permiso de lectura sobre ese modelo.
   *
   * Es la diferencia entre «no hay consorcios que se llamen asi» y «vos no ves
   * consorcios», y confundirlas hace que alguien concluya que el dato no esta
   * cargado y lo vuelva a cargar.
   */
  visible: boolean;
  resultados: CoincidenciaDeBusqueda[];
}

export interface TipoElegible {
  valor: string;
  etiqueta: string;
}

export interface RespuestaDeBusqueda {
  q: string;
  tipo: string;
  tope: number;
  total: number;
  /** Por que no se busco, si no se busco. Vacio cuando si se busco. */
  aviso: string;
  grupos: GrupoDeBusqueda[];
  tipos: TipoElegible[];
}

/** El valor de «no acotes nada». Tiene que coincidir con `services/busqueda.py`. */
export const TIPO_TODO = 'todo';

const RUTA = '/consorcios/buscar/';

/**
 * Busca en los tres niveles de una sola vez.
 *
 * Nunca falla por consulta corta: el servidor contesta la forma vacia con un
 * aviso de que escribir. Es lo que permite que la pantalla pida esto al montarse,
 * antes de que la persona haya tipeado nada, sin tratar un caso normal como error.
 */
export function buscarEnLaCartera(
  consulta: string,
  tipo: string,
  signal?: AbortSignal,
): Promise<RespuestaDeBusqueda> {
  return api.fetchReadOnly<RespuestaDeBusqueda>(RUTA, { q: consulta, tipo }, signal);
}

/**
 * ¿Esta respuesta es una busqueda de verdad?
 *
 * No es paranoia de tipos: el backend falso del archivo suelto contesta
 * `{detail, results: [], count: 0}` con estado 200 a toda ruta que no tenga
 * grabada, asi que «llego sin error» no alcanza. Con esto, un despliegue viejo o
 * una foto sin el endpoint muestra «esta parte no esta disponible» en vez de una
 * pantalla que dice para siempre «sin resultados», que es una mentira mas cara.
 */
export function esRespuestaValida(datos: unknown): datos is RespuestaDeBusqueda {
  if (datos === null || typeof datos !== 'object') return false;
  const posible = datos as Partial<RespuestaDeBusqueda>;
  return Array.isArray(posible.grupos) && Array.isArray(posible.tipos);
}
